
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Grid,
    IconButton,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Chip,
    CircularProgress,
    Avatar,
    AvatarGroup,
    InputAdornment,
    TablePagination,
    useTheme,
    alpha,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Skeleton
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon, Image as ImageIcon, Warning as WarningIcon, Close as CloseIcon, Search as SearchIcon, Visibility as ViewIcon, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { eventService } from '../service/eventService';
import { getImageUrl } from '../service/api';
import SuccessNotification from '../components/SuccessNotification';

export default function Events() {
    const theme = useTheme();
    // Character limits
    const MAX_TITLE_LENGTH = 150;
    const MAX_DESCRIPTION_LENGTH = 2500;
    const IMAGE_WIDTH = 800;
    const IMAGE_HEIGHT = 600;

    const [events, setEvents] = useState([]);
    const [open, setOpen] = useState(false);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [imagePreviewOpen, setImagePreviewOpen] = useState(false);
    const [openDatePicker, setOpenDatePicker] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedEventId, setSelectedEventId] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [selectedImage, setSelectedImage] = useState('');
    const [previewImages, setPreviewImages] = useState([]);
    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [eventAnchorEl, setEventAnchorEl] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        tag: '',
        location: '',
        eventDate: '',
        images: []
    });

    // Pagination & Search state
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');

    // Notification State
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });

    // Validation State
    const [errors, setErrors] = useState({
        title: ''
    });
    const [touched, setTouched] = useState({
        title: false
    });

    useEffect(() => {
        fetchEvents();
    }, []);

    const fetchEvents = async () => {
        setLoading(true);
        try {
            // Ensure loading lasts for at least 1 second for smooth skeleton transition
            const [response] = await Promise.all([
                eventService.getEvents(),
                new Promise(resolve => setTimeout(resolve, 1000))
            ]);
            if (response.success && response.data) {
                setEvents(response.data.data || []);
            }
        } catch (error) {
            console.error("Error fetching events", error);
        } finally {
            setLoading(false);
        }
    };

    const handleOpen = () => {
        setIsEditing(false);
        setOpen(true);
        setPreviewImages([]);
        setFormData({
            title: '',
            description: '',
            tag: '',
            location: '',
            eventDate: dayjs(),
            images: []
        });
        setErrors({
            title: '',
            // description: '',
            // location: '',
            // eventDate: ''
        });
        setTouched({
            title: false,
            // description: false,
            // location: false,
            // eventDate: false
        });
    };

    const handleEditOpen = (event) => {
        setIsEditing(true);
        setSelectedEventId(event.id);

        setFormData({
            title: event.title || '',
            description: event.description || '',
            tag: event.tag || '',
            location: event.location || '',
            eventDate: event.eventDate ? dayjs(event.eventDate) : null,
            images: []
        });

        // Load existing images for preview if any
        if (event.imagesUrl && Array.isArray(event.imagesUrl)) {
            setPreviewImages(event.imagesUrl.map(url => getImageUrl(url)));
        } else {
            setPreviewImages([]);
        }

        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setIsEditing(false);
        setSelectedEventId(null);
        setFormData({
            title: '',
            description: '',
            tag: '',
            location: '',
            eventDate: null,
            images: []
        });
        setPreviewImages([]);
        setErrors({
            title: '',
            description: '',
            location: '',
            eventDate: ''
        });
        setTouched({
            title: false,
            description: false,
            location: false,
            eventDate: false
        });
    };

    const handleChange = (e) => {
        const { name, value } = e.target;

        // Apply character length limits
        if (name === 'title' && value.length > MAX_TITLE_LENGTH) return;
        if (name === 'description' && value.length > MAX_DESCRIPTION_LENGTH) return;

        setFormData(prev => ({ ...prev, [name]: value }));

        // Clear error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleBlur = (field) => {
        setTouched(prev => ({ ...prev, [field]: true }));
        validateField(field, formData[field]);
    };

    const validateField = (field, value) => {
        let error = '';

        // Only validate title as required
        if (field === 'title') {
            if (!value || value.trim() === '') {
                error = 'Event title is required';
            }
        }

        setErrors(prev => ({ ...prev, [field]: error }));
        return error === '';
    };

    const handleDateChange = (newValue) => {
        setFormData(prev => ({ ...prev, eventDate: newValue }));
    };

    const validateForm = () => {
        const newErrors = {
            title: ''
        };

        let isValid = true;

        // Only validate title as required
        if (!formData.title || formData.title.trim() === '') {
            newErrors.title = 'Event title is required';
            isValid = false;
        }

        setErrors(newErrors);
        setTouched({
            title: true
        });

        return isValid;
    };

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        setFormData(prev => ({ ...prev, images: files }));

        // Create local preview URLs for selected files
        const newPreviews = files.map(file => URL.createObjectURL(file));
        setPreviewImages(newPreviews);
    };

    const handleSubmit = async () => {
        // Validate form before submission
        if (!validateForm()) {
            setNotification({ open: true, message: 'Event title is required', severity: 'error' });
            return;
        }

        setLoading(true);
        try {
            const data = new FormData();
            data.append('Title', formData.title);
            data.append('Description', formData.description);
            data.append('Tag', formData.tag);
            data.append('Location', formData.location);
            // Convert dayjs object to ISO string
            const dateStr = formData.eventDate ? formData.eventDate.toISOString() : '';
            data.append('EventDate', dateStr);

            // Append images
            formData.images.forEach((file) => {
                data.append('Images', file);
            });

            let response;
            if (isEditing && selectedEventId) {
                response = await eventService.updateEvent(selectedEventId, data);
            } else {
                response = await eventService.createEvent(data);
            }

            if (response.success) {
                fetchEvents();
                handleClose();
                setNotification({ open: true, message: `Event ${isEditing ? 'updated' : 'created'} successfully!`, severity: 'success' });
            } else {
                alert(response.message || "Operation failed");
            }
        } catch (error) {
            console.error("Error saving event", error);
            alert("Error saving event");
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteClick = (id) => {
        setSelectedEventId(id);
        setDeleteModalOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (!selectedEventId) return;
        setLoading(true);
        try {
            await eventService.deleteEvent(selectedEventId);
            fetchEvents();
            setDeleteModalOpen(false);
            setSelectedEventId(null);
            setNotification({ open: true, message: 'Event deleted successfully!', severity: 'success' });
        } catch (error) {
            console.error("Error deleting event", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteModalOpen(false);
        setSelectedEventId(null);
    };

    const handleViewOpen = (event) => {
        setSelectedEvent(event);
        setViewModalOpen(true);
    };

    const handleImagePreview = (imageUrl) => {
        setSelectedImage(imageUrl);
        setImagePreviewOpen(true);
    };

    const handleEventMenuOpen = (event, eventData) => {
        setEventAnchorEl(event.currentTarget);
        setSelectedEvent(eventData);
    };

    const handleEventMenuClose = () => {
        setEventAnchorEl(null);
        setSelectedEvent(null);
    };

    return (
        <Box>
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: { xs: 3, sm: 4 },
                p: { xs: 2, sm: 3 },
                gap: 2,
                borderRadius: 4,
                bgcolor: 'white',
                boxShadow: '0 2px 12px rgba(0,0,0,0.03)'
            }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>Events</Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleOpen}
                    sx={{
                        borderRadius: 2.5,
                        px: 3,
                        py: 1.2,
                        width: { xs: '100%', sm: 'auto' },
                        textTransform: 'none',
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        background: 'primary.main',
                        boxShadow: `0 {4}px 14px 0 ${alpha(theme.palette.secondary.main, 0.39)}`,
                        '&:hover': {
                            background: 'primary.dark',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.secondary.main, 0.23)}`
                        }
                    }}
                >
                    Add Event
                </Button>
            </Box>

            <Paper sx={{
                borderRadius: 4,
                overflow: 'hidden',
                boxShadow: '0 10px 30px 0 rgba(0,0,0,0.04)',
                border: '1px solid rgba(226, 232, 240, 0.8)',
                mb: 4
            }}>
                <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: '1px solid', borderColor: '#f1f5f9', bgcolor: '#ffffff' }}>
                    <TextField
                        fullWidth
                        placeholder="Search events by title, tag or location..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(0);
                        }}
                        variant="outlined"
                        size="small"
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon fontSize="small" sx={{ color: '#94a3b8' }} />
                                </InputAdornment>
                            ),
                            sx: {
                                bgcolor: '#f8fafc',
                                borderRadius: 3,
                                '& fieldset': { border: 'none' },
                                '&:hover': { bgcolor: '#f1f5f9' },
                                transition: 'all 0.2s'
                            }
                        }}
                    />
                </Box>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table sx={{ minWidth: 800 }}>
                        <TableHead sx={{ bgcolor: '#f8fafc' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase', py: 2 }}>Actions</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Images</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Title</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Date</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Location</TableCell>
                                <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.85rem', textTransform: 'uppercase' }}>Tag</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {loading ? (
                                Array.from(new Array(rowsPerPage)).map((_, index) => (
                                    <TableRow key={`skeleton-${index}`}>
                                        <TableCell><Skeleton variant="circular" width={32} height={32} /></TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <Skeleton variant="circular" width={40} height={40} />
                                                <Skeleton variant="circular" width={40} height={40} />
                                            </Box>
                                        </TableCell>
                                        <TableCell><Skeleton width="80%" /></TableCell>
                                        <TableCell><Skeleton width="60%" /></TableCell>
                                        <TableCell><Skeleton width="70%" /></TableCell>
                                        <TableCell><Skeleton width="40%" height={24} sx={{ borderRadius: 1.5 }} /></TableCell>
                                    </TableRow>
                                ))
                            ) : events
                                .filter(event =>
                                    (event.title?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                    (event.location?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                    (event.tag?.toLowerCase().includes(searchTerm.toLowerCase()))
                                )
                                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                .map((event) => {
                                    const isCompleted = dayjs(event.eventDate).isBefore(dayjs());
                                    return (
                                        <TableRow
                                            key={event.id}
                                            hover
                                            sx={{
                                                '&:last-child td, &:last-child th': { border: 0 },
                                                bgcolor: isCompleted ? alpha(theme.palette.primary.main, 0.04) : 'inherit',
                                                '&:hover': { bgcolor: isCompleted ? alpha(theme.palette.primary.main, 0.08) : 'rgba(241, 245, 249, 0.5) !important' },
                                                transition: 'all 0.2s'
                                            }}
                                        >
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleEventMenuOpen(e, event)}
                                                    sx={{ color: '#64748b' }}
                                                >
                                                    <MoreVertIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                            <TableCell>
                                                <AvatarGroup max={3} sx={{
                                                    justifyContent: 'flex-start',
                                                    '& .MuiAvatar-root': { width: 44, height: 44, border: '2px solid white', boxShadow: '0 2px 6px rgba(0,0,0,0.1)' }
                                                }}>
                                                    {event.imagesUrl && event.imagesUrl.length > 0 ? (
                                                        event.imagesUrl.map((url, index) => (
                                                            <Avatar
                                                                key={index}
                                                                src={getImageUrl(url)}
                                                                alt={event.title}
                                                                sx={{ cursor: 'pointer' }}
                                                                onClick={() => handleImagePreview(getImageUrl(url))}
                                                            />
                                                        ))
                                                    ) : (
                                                        <Avatar sx={{ bgcolor: '#f1f5f9', color: '#94a3b8', fontSize: '0.75rem' }}>N/A</Avatar>
                                                    )}
                                                </AvatarGroup>
                                            </TableCell>
                                            <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{event.title}</TableCell>
                                            <TableCell sx={{ fontWeight: 500, color: '#64748b' }}>{new Date(event.eventDate).toLocaleDateString()}</TableCell>
                                            <TableCell sx={{ color: '#64748b' }}>{event.location}</TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={event.tag}
                                                    size="small"
                                                    color="primary"
                                                    variant="filled"
                                                    sx={{
                                                        borderRadius: 1.5,
                                                        fontWeight: 600,
                                                        fontSize: '0.7rem',
                                                        height: 24,
                                                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                        color: theme.palette.primary.main,
                                                        border: 'none'
                                                    }}
                                                />
                                                {isCompleted && (
                                                    <Chip
                                                        label="Completed"
                                                        size="small"
                                                        sx={{
                                                            ml: 1,
                                                            borderRadius: 1.5,
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            height: 24,
                                                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                            color: theme.palette.primary.main,
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            {events.filter(event =>
                                (event.title?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                (event.location?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                                (event.tag?.toLowerCase().includes(searchTerm.toLowerCase()))
                            ).length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={6} align="center" sx={{ py: 6, color: 'text.disabled' }}>
                                            No events found.
                                        </TableCell>
                                    </TableRow>
                                )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25]}
                        component="div"
                        count={events.filter(event =>
                            (event.title?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (event.location?.toLowerCase().includes(searchTerm.toLowerCase())) ||
                            (event.tag?.toLowerCase().includes(searchTerm.toLowerCase()))
                        ).length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={(e, p) => setPage(p)}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                    />
                </TableContainer>
            </Paper>

            {/* Create/Edit Modal */}
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>{isEditing ? 'Edit Event' : 'Add New Event'}</DialogTitle>
                <DialogContent>
                    <Box component="form" sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                        <TextField
                            label="Event Title"
                            name="title"
                            fullWidth
                            value={formData.title}
                            onChange={handleChange}
                            onBlur={() => handleBlur('title')}
                            required
                            variant="outlined"
                            inputProps={{ maxLength: MAX_TITLE_LENGTH }}
                            error={touched.title && !!errors.title}
                            helperText={
                                touched.title && errors.title
                                    ? errors.title
                                    : `${formData.title.length}/${MAX_TITLE_LENGTH} characters`
                            }
                        />
                        <TextField
                            label="Description"
                            name="description"
                            multiline
                            rows={3}
                            fullWidth
                            value={formData.description}
                            onChange={handleChange}
                            variant="outlined"
                            inputProps={{ maxLength: MAX_DESCRIPTION_LENGTH }}
                            helperText={`${formData.description.length}/${MAX_DESCRIPTION_LENGTH} characters`}
                        />
                        <Grid container spacing={2}>

                            <TextField
                                label="Tag"
                                name="tag"
                                fullWidth
                                value={formData.tag}
                                onChange={handleChange}
                                variant="outlined"
                            />

                            <TextField
                                label="Location"
                                name="location"
                                fullWidth
                                value={formData.location}
                                onChange={handleChange}
                                variant="outlined"
                            />
                        </Grid>
                        <DateTimePicker
                            label="Event Date & Time"
                            value={formData.eventDate}
                            onChange={handleDateChange}
                            open={openDatePicker}
                            onOpen={() => setOpenDatePicker(true)}
                            onClose={() => setOpenDatePicker(false)}
                            sx={{ width: '100%' }}
                            slotProps={{
                                textField: {
                                    fullWidth: true,
                                    variant: 'outlined',
                                    onClick: (e) => setOpenDatePicker(true)
                                },
                                popper: {
                                    sx: { zIndex: 1301 },
                                    placement: 'bottom-start'
                                }
                            }}
                        />

                        <Button
                            variant="outlined"
                            component="label"
                            startIcon={<ImageIcon />}
                            sx={{ borderStyle: 'dashed', borderRadius: 2, height: 64, color: 'text.secondary', borderColor: 'rgba(0,0,0,0.12)' }}
                        >
                            {isEditing ? 'Upload New Images (Optional)' : 'Upload Images'}
                            <input
                                type="file"
                                hidden
                                multiple
                                accept="image/*"
                                onChange={handleFileChange}
                            />
                        </Button>

                        {/* Image Previews in Modal */}
                        {previewImages.length > 0 && (
                            <Box sx={{ mt: -1.5 }}>
                                <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary', fontWeight: 500, display: 'block', mb: 1 }}>
                                    {previewImages.length} images selected/available
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {previewImages.map((src, index) => (
                                        <Box
                                            key={index}
                                            component="img"
                                            src={src}
                                            alt={`preview-${index}`}
                                            sx={{ width: 80, height: 60, borderRadius: 2, objectFit: 'cover', border: '1px solid #e2e8f0' }}
                                        />
                                    ))}
                                </Box>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 1 }}>
                    <Button onClick={handleClose} color="inherit" sx={{ fontWeight: 600 }}>Cancel</Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={loading}
                        sx={{ px: 3, py: 1, borderRadius: 2, fontWeight: 600, boxShadow: 'none' }}
                    >
                        {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : (isEditing ? 'Save Changes' : 'Create Event')}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog
                open={deleteModalOpen}
                onClose={handleDeleteCancel}
                maxWidth="xs"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', pt: 4 }}>
                    <Box sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: 'rgba(239, 68, 68, 0.1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mb: 2
                    }}>
                        <DeleteIcon sx={{ fontSize: 32, color: 'error.main' }} />
                    </Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: '#1e293b' }}>
                        Delete Event?
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Are you sure you want to delete this event? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, justifyContent: 'center', gap: 2 }}>
                    <Button
                        onClick={handleDeleteCancel}
                        variant="outlined"
                        color="inherit"
                        sx={{ borderRadius: 2, flex: 1, fontWeight: 600, borderColor: 'rgba(0,0,0,0.12)' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDeleteConfirm}
                        variant="contained"
                        color="error"
                        disabled={loading}
                        sx={{ borderRadius: 2, flex: 1, fontWeight: 600, boxShadow: 'none' }}
                    >
                        {loading ? <CircularProgress size={20} color="inherit" /> : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Full Image Preview Modal */}
            <Dialog
                open={imagePreviewOpen}
                onClose={() => setImagePreviewOpen(false)}
                maxWidth="md"
                PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden', bgcolor: 'black' } }}
            >
                <Box sx={{ position: 'relative' }}>
                    <IconButton
                        onClick={() => setImagePreviewOpen(false)}
                        sx={{ position: 'absolute', right: 8, top: 8, color: 'white', bgcolor: 'rgba(0,0,0,0.5)', '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' } }}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Box
                        component="img"
                        src={selectedImage}
                        sx={{
                            width: '100%',
                            maxWidth: `${IMAGE_WIDTH}px`,
                            maxHeight: '80vh',
                            objectFit: 'contain',
                            display: 'block',
                            margin: '0 auto'
                        }}
                    />
                </Box>
            </Dialog>

            {/* View Event Modal */}
            <Dialog
                open={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                maxWidth="sm"
                fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}
            >
                <DialogTitle sx={{ fontWeight: 700, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    Event Details
                    <IconButton onClick={() => setViewModalOpen(false)} size="small">
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent dividers>
                    {selectedEvent && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                            {selectedEvent.imagesUrl && selectedEvent.imagesUrl.length > 0 && (
                                <Box sx={{ display: 'flex', gap: 1, overflowX: 'auto', pb: 1 }}>
                                    {selectedEvent.imagesUrl.map((url, idx) => (
                                        <Box
                                            key={idx}
                                            component="img"
                                            src={getImageUrl(url)}
                                            sx={{ height: 150, borderRadius: 2, objectFit: 'cover', cursor: 'pointer' }}
                                            onClick={() => handleImagePreview(getImageUrl(url))}
                                        />
                                    ))}
                                </Box>
                            )}

                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Title</Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedEvent.title}</Typography>
                            </Box>

                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Description</Typography>
                                <Typography variant="body1">{selectedEvent.description || 'No description available.'}</Typography>
                            </Box>

                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Date & Time</Typography>
                                    <Typography variant="body1">{new Date(selectedEvent.eventDate).toLocaleString()}</Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Location</Typography>
                                    <Typography variant="body1">{selectedEvent.location || 'Not specified'}</Typography>
                                </Grid>
                            </Grid>

                            <Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, textTransform: 'uppercase' }}>Tag</Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip label={selectedEvent.tag} color="primary" variant="outlined" size="small" />
                                </Box>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setViewModalOpen(false)} variant="contained" fullWidth sx={{ borderRadius: 2 }}>
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            <Menu
                anchorEl={eventAnchorEl}
                open={Boolean(eventAnchorEl)}
                onClose={handleEventMenuClose}
                PaperProps={{
                    sx: { minWidth: 150, borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }
                }}
            >
                <MenuItem onClick={() => { handleEventMenuClose(); handleViewOpen(selectedEvent); }}>
                    <ListItemIcon><ViewIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText primary="View" />
                </MenuItem>
                <MenuItem onClick={() => { handleEventMenuClose(); handleEditOpen(selectedEvent); }}>
                    <ListItemIcon><EditIcon fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText primary="Edit" />
                </MenuItem>
                <MenuItem onClick={() => { handleEventMenuClose(); handleDeleteClick(selectedEvent.id); }} sx={{ color: 'error.main' }}>
                    <ListItemIcon><DeleteIcon fontSize="small" color="error" /></ListItemIcon>
                    <ListItemText primary="Delete" />
                </MenuItem>
            </Menu>

            <SuccessNotification
                open={notification.open}
                message={notification.message}
                severity={notification.severity}
                onClose={() => setNotification({ ...notification, open: false })}
            />
        </Box>
    );
}
